// API Service Layer - Enterprise Level Implementation
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  status?: number;
  pagination?: {
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    limit?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
    nextPage?: number | null;
    prevPage?: number | null;
  };
}

export interface LoginRequest {
  // One of identifier | email | phone plus password
  identifier?: string;
  email?: string;
  phone?: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    vendor: {
      _id: string;
      name: string;
      email: string;
      phone: string;
      address: string;
      profileImageUrl?: string;
    };
    token?: string;
  };
}

export interface Driver {
  _id?: string;
  driverName?: string; // For frontend display
  name?: string; // From API response
  email?: string;
  mobileNumber?: string; // For frontend
  phoneNumber?: string; // From API response
  drivingLicense?: string; // For frontend
  licenseNumber?: string; // From API response
  address: string;
  dlFile?: File | string;
  profilePicture?: File | string; // Profile picture file or URL
  status?: 'Active' | 'Inactive';
  assignedVehicles?: string;
  lastTrip?: string;
}

export interface VehicleType {
  _id: string;
  name: string;
  description?: string;
  capacity?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Vehicle {
  _id?: string;
  vehicleNumber: string;
  vehicleType: string;
  vehicleInsurance: string;
  vehicleInsuranceNumber: string;
  vehicleInsuranceExpiry: string; // YYYY-MM-DD format
  vehiclePermit?: string;
  vehiclePermitNumber: string;
  vehiclePermitExpiry: string; // YYYY-MM-DD format
  vehiclePollution?: string;
  vehiclePollutionNumber: string;
  vehiclePollutionExpiry: string; // YYYY-MM-DD format
  vehicleRc?: string;
  vehicleRCNumber: string;
  vehicleRcExpiry: string; // YYYY-MM-DD format
  // File uploads
  vehicleImage?: File | string;
  vehicleInsuranceImage?: File | string;
  vehiclePermitImage?: File | string;
  vehiclePollutionImage?: File | string;
  vehicleRcImage?: File | string;
  // Additional fields
  capacity?: string;
  status?: 'Active' | 'Inactive';
  assignedDriver?: string;
  lastMaintenance?: string;
  contractedHours?: number;
  contractedKms?: number;
  isElixia?: boolean;
}

export interface DriverPerformance {
  name: string;
  deliveries: number;
  rating: number;
  efficiency: number;
  color: string;
  profileImageUrl?: string;
}

// Define the response structure for the new driver performance API
export interface GetDriverPerformanceResponse {
  topDrivers: DriverPerformance[];
}

export interface GetEnhancedDriverReportResponse {
  drivers: Array<{
    _id: string;
    name: string;
    phoneNumber: string;
    licenseNumber: string;
    licenseExpiry: string;
    isActive: boolean;
    totalTrips: number;
    completedTrips: number;
    onTimePercentage: number;
    complaints: number;
    createdAt: string;
  }>;
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

export interface CheckVehicleInElixiaResponse {
  status: boolean;
  isElixiaPresent: boolean;
}

export interface InvoiceData {
  invoiceNumber?: string;
  date?: string;
  vendorName?: string;
  amount?: number;
  tax?: number;
  total?: number;
  items?: any[];
  status?: string;
  [key: string]: any;
}

export interface Invoice extends InvoiceData {
  _id: string;
  createdAt?: string;
  updatedAt?: string;
}


export class ApiService {
  private baseURL: string;
  private invoiceBaseURL: string = "https://vendor-invoice.vercel.app/api";
  private token: string | null = null;

  constructor() {
    const envUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/vendors';
    // Vendor portal must hit /api/vendors (e.g. /api/vendors/auth/login). If env is only /api, append /vendors.
    this.baseURL = envUrl.replace(/\/api\/?$/, '/api/vendors');
    this.token = localStorage.getItem('authToken');
  }


  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // Always check localStorage for the latest token (in case it was updated)
    const token = localStorage.getItem('authToken') || this.token;
    if (token) {
      this.token = token; // Update the instance variable
    }

    const defaultHeaders: HeadersInit = {
      'Accept': 'application/json',
    };

    // Only set Content-Type for JSON requests, not for FormData
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      mode: 'cors', // Explicitly set CORS mode
      credentials: 'include', // Send credentials to match server CORS config
    };

    try {
      console.log('Making request to:', url);
      console.log('Request config:', config);
      console.log('Body type:', options.body instanceof FormData ? 'FormData' : typeof options.body);
      console.log('Token available:', !!token);
      console.log('Token value:', token ? token.substring(0, 20) + '...' : 'none');
      console.log('CORS mode:', config.mode);
      console.log('Credentials:', config.credentials);

      const response = await fetch(url, config);

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      // Check for network errors
      if (!response.ok && response.status === 0) {
        throw new Error('Network error: Unable to connect to server. Please check if the server is running.');
      }

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Handle 401 Unauthorized - clear token and user data
        if (response.status === 401) {
          console.warn('Received 401 Unauthorized - clearing token and user data');
          this.token = null;
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');

          // Dispatch a custom event that AuthContext can listen to
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse JSON, use the status text
        }

        throw new ApiError(errorMessage, response.status);
      }

      const data = await response.json();
      console.log('Response data:', data);

      return {
        success: true,
        data,
        status: response.status,
      };
    } catch (error) {
      console.error('API Request Error:', error);

      if (error instanceof ApiError) {
        return {
          success: false,
          error: error.message,
          status: error.status,
        };
      }

      // Handle CORS and network errors specifically
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          error: 'CORS error: Unable to connect to server. Please check if the server allows cross-origin requests.',
          status: 0,
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error or server unavailable',
        status: 0,
      };
    }
  }

  // Invoice API helper (uses different base URL)
  private async invoiceRequest(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<any> {
    const url = `${this.invoiceBaseURL}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        throw new Error(`Invoice API error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Invoice API Error:', error);
      throw error;
    }
  }

  // Authentication Methods
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    console.log('Login credentials:', credentials);
    console.log('Full URL will be:', `${this.baseURL}/auth/login`);
    // Build payload compatible with backend (supports email or phone via identifier)
    const payload: any = { password: credentials.password };
    const id = credentials.identifier || credentials.email || credentials.phone;
    if (id) {
      // If it's an email, send as email; else send as identifier (phone)
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(id));
      if (isEmail) payload.email = String(id).toLowerCase();
      else payload.identifier = String(id);
    }

    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    console.log('Login response:', response);
    console.log('Login response.data:', response.data);

    if (response.success && response.data) {
      // Handle the actual API response structure
      // Server returns: { success: true, message: "...", data: { vendor: {...}, token: "..." } }
      // request() wraps it as: { success: true, data: <server response> }
      const apiResponse = response.data as any;
      console.log('API Response structure:', apiResponse);

      // Try to extract token from the correct path
      let token = null;
      if (apiResponse?.data?.token) {
        token = apiResponse.data.token;
      } else if (apiResponse?.token) {
        token = apiResponse.token;
      }

      if (token) {
        console.log('Token extracted successfully, setting token');
        this.setToken(token);
      } else {
        console.error('Token not found in response:', apiResponse);
        // Don't set a dummy token - this will cause auth failures
      }
    } else {
      console.error('Login response not successful:', response);
    }

    return response;
  }

  async logout(): Promise<void> {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  async forgotPassword(email?: string, phone?: string): Promise<ApiResponse<{ success: boolean; message: string; method?: string; phone?: string }>> {
    console.log('Forgot password request - email:', email ? 'provided' : 'not provided', 'phone:', phone ? 'provided' : 'not provided');
    console.log('Forgot password request - email value:', email, 'phone value:', phone);

    const payload: any = {};
    // Only include email if it's provided and not empty
    if (email && String(email).trim()) {
      payload.email = String(email).trim();
      console.log('Adding email to payload:', payload.email);
    }
    // Only include phone if it's provided and not empty
    if (phone && String(phone).trim()) {
      payload.phone = String(phone).trim();
      console.log('Adding phone to payload:', payload.phone);
    }

    console.log('Final payload being sent:', JSON.stringify(payload));
    console.log('Request URL:', `${this.baseURL}/auth/forgot-password`);

    const response = await this.request<{ success: boolean; message: string; method?: string; phone?: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    console.log('Forgot password response:', response);
    return response;
  }

  async resetPasswordViaOTP(phone: string, otp: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    console.log('Reset password via OTP request for phone:', phone);

    const response = await this.request<{ success: boolean; message: string }>('/auth/reset-password-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, otp, newPassword, confirmPassword }),
    });

    console.log('Reset password via OTP response:', response);
    return response;
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    console.log('Reset password request with token:', token);

    const response = await this.request<{ success: boolean; message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword, confirmPassword }),
    });

    console.log('Reset password response:', response);
    return response;
  }

  async updateProfile(profileData: {
    name?: string;
    phone?: string;
    city?: string;
    state?: string;
    address?: string;
    pincode?: string;
    gstNumber?: string;
    panNumber?: string;
  }, profileImage?: File | null): Promise<ApiResponse<{ success: boolean; message: string; data?: any }>> {
    console.log('Update profile request:', profileData);

    // If there's a profile image, use FormData, otherwise use JSON
    if (profileImage) {
      const formData = new FormData();

      // Add text fields
      Object.entries(profileData).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          formData.append(key, value);
        }
      });

      // Add profile image
      formData.append('profileImage', profileImage);

      console.log('FormData contents for updateProfile:');
      const formDataArray = Array.from(formData.entries());
      formDataArray.forEach(([key, value]) => {
        console.log(`${key}:`, value);
      });

      const response = await this.request<{ success: boolean; message: string; data?: any }>('/profile/update', {
        method: 'PUT',
        body: formData,
      });

      console.log('Update profile response:', response);
      return response;
    } else {
      const response = await this.request<{ success: boolean; message: string; data?: any }>('/profile/update', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      console.log('Update profile response:', response);
      return response;
    }
  }

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Driver Management Methods
  async getDrivers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    data: Driver[];
    totalDrivers?: number;
    pagination?: {
      currentPage: number;
      totalPages: number;
      totalDrivers: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting drivers from API...');
    let endpoint = '/drivers';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get drivers response:', response);
    return response;
  }

  async addDriver(driver: Omit<Driver, 'id'>): Promise<ApiResponse<Driver>> {
    console.log('Adding driver:', driver);

    const formData = new FormData();

    // Add text fields - using the correct field names from API response
    formData.append('name', driver.driverName || '');
    formData.append('email', driver.email || `${(driver.driverName || 'driver').toLowerCase().replace(/\s+/g, '.')}@abclogistics.com`);
    formData.append('address', driver.address || '');
    formData.append('drivingLicense', driver.drivingLicense || '');
    formData.append('phoneNumber', driver.mobileNumber || '');

    // Add files if present
    if (driver.dlFile && driver.dlFile instanceof File) {
      formData.append('drivingLicenseImage', driver.dlFile);
    }

    if (driver.profilePicture && driver.profilePicture instanceof File) {
      formData.append('profilePicture', driver.profilePicture);
    }

    console.log('FormData entries:');
    // Convert FormData to array for logging
    const formDataArray = Array.from(formData.entries());
    formDataArray.forEach(([key, value]) => {
      console.log(`${key}:`, value);
    });

    console.log('Driver object being sent:', {
      name: driver.driverName || '',
      email: driver.email || `${(driver.driverName || 'driver').toLowerCase().replace(/\s+/g, '.')}@abclogistics.com`,
      address: driver.address || '',
      licenseNumber: driver.drivingLicense || '',
      phoneNumber: driver.mobileNumber || ''
    });

    return this.request<Driver>('/drivers', {
      method: 'POST',
      body: formData,
    });
  }

  async updateDriver(id: string, driver: Partial<Driver>): Promise<ApiResponse<Driver>> {
    console.log('Updating driver:', id, driver);

    const formData = new FormData();

    // Add text fields - using the correct field names from API
    if (driver.name) formData.append('name', driver.name);
    if (driver.email) formData.append('email', driver.email);
    if (driver.phoneNumber) formData.append('phoneNumber', driver.phoneNumber);
    if (driver.drivingLicense) formData.append('drivingLicense', driver.drivingLicense);
    if (driver.address) formData.append('address', driver.address);

    // Add file uploads if present
    if (driver.dlFile && driver.dlFile instanceof File) {
      formData.append('drivingLicenseImage', driver.dlFile);
    }

    // Add profile picture if present
    if (driver.profilePicture && driver.profilePicture instanceof File) {
      formData.append('profilePicture', driver.profilePicture);
    }

    // Log FormData contents for debugging
    console.log('FormData contents for updateDriver:');
    const formDataArray = Array.from(formData.entries());
    formDataArray.forEach(([key, value]) => {
      console.log(`${key}:`, value);
    });

    return this.request<Driver>(`/drivers/${id}`, {
      method: 'PUT',
      body: formData,
    });
  }

  async deleteDriver(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/drivers/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteDrivers(driverIds: string[]): Promise<ApiResponse<{ deletedCount: number }>> {
    console.log('Bulk deleting drivers:', driverIds);
    const response = await this.request<{ deletedCount: number }>('/drivers/bulk/delete', {
      method: 'DELETE',
      body: JSON.stringify({ driverIds }),
    });
    console.log('Bulk delete drivers response:', response);
    return response;
  }

  // Vehicle Management Methods
  async getVehicles(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    data: Vehicle[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalVehicles: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting vehicles from API...');
    let endpoint = '/vehicles';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get vehicles response:', response);
    return response;
  }

  async addVehicle(vehicle: Omit<Vehicle, 'id'>): Promise<ApiResponse<Vehicle>> {
    console.log('Adding vehicle:', vehicle);

    const formData = new FormData();

    // ✅ Add required fields (with default empty strings)
    formData.append('vehicleNumber', vehicle.vehicleNumber || '');
    formData.append('vehicleType', vehicle.vehicleType || '');
    formData.append('vehicleInsurance', vehicle.vehicleInsurance || '');
    formData.append('vehicleInsuranceNumber', vehicle.vehicleInsuranceNumber || '');
    formData.append('vehicleInsuranceExpiry', vehicle.vehicleInsuranceExpiry || '');
    formData.append('vehiclePermit', vehicle.vehiclePermit || '');
    formData.append('vehiclePermitNumber', vehicle.vehiclePermitNumber || '');
    formData.append('vehiclePermitExpiry', vehicle.vehiclePermitExpiry || '');
    formData.append('vehiclePollution', vehicle.vehiclePollution || '');
    formData.append('vehiclePollutionNumber', vehicle.vehiclePollutionNumber || '');
    formData.append('vehiclePollutionExpiry', vehicle.vehiclePollutionExpiry || '');
    formData.append('vehicleRc', vehicle.vehicleRc || '');
    formData.append('vehicleRCNumber', vehicle.vehicleRCNumber || '');
    formData.append('vehicleRcExpiry', vehicle.vehicleRcExpiry || '');

    // ✅ Add optional simple fields
    if (vehicle.capacity) formData.append('capacity', vehicle.capacity);
    if (vehicle.status) formData.append('status', vehicle.status);
    if (vehicle.assignedDriver) formData.append('assignedDriver', vehicle.assignedDriver);

    // ✅ Correct contractedHours and contractedKms check
    if (typeof vehicle.contractedHours === 'number' && !isNaN(vehicle.contractedHours)) {
      formData.append('contractedHours', String(vehicle.contractedHours));
    }
    if (typeof vehicle.contractedKms === 'number' && !isNaN(vehicle.contractedKms)) {
      formData.append('contractedKms', String(vehicle.contractedKms));
    }

    // ✅ Add file uploads if present
    if (vehicle.vehicleImage instanceof File) {
      formData.append('vehicleImage', vehicle.vehicleImage);
    }
    if (vehicle.vehicleInsuranceImage instanceof File) {
      formData.append('vehicleInsuranceImage', vehicle.vehicleInsuranceImage);
    }
    if (vehicle.vehiclePermitImage instanceof File) {
      formData.append('vehiclePermitImage', vehicle.vehiclePermitImage);
    }
    if (vehicle.vehiclePollutionImage instanceof File) {
      formData.append('vehiclePollutionImage', vehicle.vehiclePollutionImage);
    }
    if (vehicle.vehicleRcImage instanceof File) {
      formData.append('vehicleRcImage', vehicle.vehicleRcImage);
    }

    // // ✅ Debug log for clarity
    // console.log('FormData contents for addVehicle:');
    // for (const [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }

    console.log('Final vehicle payload being sent:', {
      vehicleNumber: vehicle.vehicleNumber,
      vehicleType: vehicle.vehicleType,
      contractedHours: vehicle.contractedHours,
      contractedKms: vehicle.contractedKms,
    });

    // ✅ API call
    return this.request<Vehicle>('/vehicles', {
      method: 'POST',
      body: formData,
    });
  }


  async updateVehicle(vehicleId: string, vehicle: Vehicle): Promise<ApiResponse<Vehicle>> {
    console.log("Updating vehicle:", vehicle);

    const hasFile = Object.values(vehicle).some(v => v instanceof File);

    if (hasFile) {
      const formData = new FormData();

      Object.entries(vehicle).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          // Convert numbers to strings explicitly to preserve correct data
          if (typeof value === "number") {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value);
          }
        }
      });

      console.log("FormData contents for updateVehicle:");
      Array.from(formData.entries()).forEach(([key, val]) => console.log(`${key}:`, val));

      return this.request<Vehicle>(`/vehicles/${vehicleId}`, {
        method: "PUT",
        body: formData,
      });
    } else {
      // If no files, send as JSON
      const cleanVehicle = { ...vehicle };
      // Convert numeric string fields to numbers
      if (cleanVehicle.contractedHours) cleanVehicle.contractedHours = Number(cleanVehicle.contractedHours);
      if (cleanVehicle.contractedKms) cleanVehicle.contractedKms = Number(cleanVehicle.contractedKms);

      return this.request<Vehicle>(`/vehicles/${vehicleId}`, {
        method: "PUT",
        body: JSON.stringify(cleanVehicle),
      });
    }
  }


  async deleteVehicle(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/vehicles/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteVehicles(vehicleIds: string[]): Promise<ApiResponse<{ deletedCount: number }>> {
    console.log('Bulk deleting vehicles:', vehicleIds);
    const response = await this.request<{ deletedCount: number }>('/vehicles/bulk/delete', {
      method: 'DELETE',
      body: JSON.stringify({ vehicleIds }),
    });
    console.log('Bulk delete vehicles response:', response);
    return response;
  }

  // Vehicle Types Management Methods
  async getVehicleTypes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<string[]>> {
    console.log('Getting vehicle types from API...');

    let endpoint = '/vehicle-types';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<string[]>(endpoint);

    console.log('Get vehicle types response:', response);
    return response;
  }

  // Dashboard Data
  async getDashboardData(): Promise<ApiResponse<{
    metrics: {
      activeDrivers: number;
      fleetVehicles: number;
      coldChainDeliveries: number;
      temperatureViolations: number;
    };
    recentDrivers: Driver[];
  }>> {
    return this.request('/dashboard');
  }

  // Vendor Invoices (from Finops) - list, accept, reject
  // Endpoint must be under /api/vendors/invoices (backend route)
  private getVendorInvoicesEndpoint(path: string): string {
    const base = this.baseURL.endsWith('/vendors') ? '' : '/vendors';
    return `${base}${path}`;
  }

  async getMyVendorInvoices(params?: { page?: number; limit?: number; status?: string }): Promise<ApiResponse<{
    data: Array<{
      _id: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate: string;
      totalAmount: number;
      status: string;
      vendorBillingData?: { vendorName?: string };
      pdfPath?: string;
    }>;
    pagination: { page: number; limit: number; total: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean };
  }>> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    const query = q.toString();
    const path = `/invoices${query ? `?${query}` : ''}`;
    return this.request(this.getVendorInvoicesEndpoint(path));
  }

  async acknowledgeVendorInvoice(invoiceId: string): Promise<ApiResponse<{ data?: { invoice: any } }>> {
    return this.request(this.getVendorInvoicesEndpoint(`/invoices/${invoiceId}/acknowledge`), { method: 'PATCH' });
  }

  async rejectVendorInvoice(invoiceId: string, description?: string): Promise<ApiResponse<{ data?: { invoice: any; disputeId: string } }>> {
    return this.request(this.getVendorInvoicesEndpoint(`/invoices/${invoiceId}/reject`), {
      method: 'PATCH',
      body: JSON.stringify(description ? { description } : {}),
    });
  }

  /** Get PDF URL for view or download */
  async getVendorInvoicePdfUrl(invoiceId: string): Promise<ApiResponse<{ data?: { pdfUrl: string; invoiceNumber: string } }>> {
    return this.request(this.getVendorInvoicesEndpoint(`/invoices/${invoiceId}/pdf`));
  }

  // Vendor Delivery Report APIs
  async getDeliveryReport(reportDate?: string): Promise<ApiResponse<{
    vendor: {
      id: string;
      name: string;
      email: string;
      phone: string;
      city: string;
      state: string;
    };
    reportDate: string;
    summary: {
      totalVehicles: number;
      vehiclesWithAssignments: number;
      totalAssignments: number;
      totalCompletedAssignments: number;
      totalPendingAssignments: number;
      totalInTransitAssignments: number;
      completionRate: string;
    };
    vehicles: Array<{
      vehicleNumber: string;
      vehicleType: string;
      totalAssignments: number;
      completedAssignments: number;
      pendingAssignments: number;
      inTransitAssignments: number;
      completionRate: string;
      assignments: Array<{
        assignmentId: string;
        companyName: string;
        storeName: string;
        startPoint: string;
        endPoint: string;
        pickupTime: string;
        deliveryStatus: string;
        deliveryCompleted: boolean;
        createdAt: string;
      }>;
    }>;
  }>> {
    const endpoint = reportDate
      ? `/delivery-report?reportDate=${reportDate}`
      : '/delivery-report';

    return this.request(endpoint);
  }

  // Driver Statistics APIs
  async getDriverStats(): Promise<ApiResponse<{
    totalDrivers: number;
    activeDrivers: number;
    inactiveDrivers: number;
    driversWithExpiringLicense: number;
    driversWithValidLicense: number;
  }>> {
    return this.request('/drivers/stats');
  }

  async getDriverDetails(driverId: string): Promise<ApiResponse<{
    _id: string;
    name: string;
    phoneNumber: string;
    licenseNumber: string;
    licenseExpiry: string;
    profilePicture: string;
    drivingLicenseImage: string;
    isActive: boolean;
    createdAt: string;
  }>> {
    return this.request(`/drivers/${driverId}`);
  }

  // Vehicle Statistics APIs
  async getVehicleStats(): Promise<ApiResponse<{
    totalVehicles: number;
    activeVehicles: number;
    inactiveVehicles: number;
    vehiclesWithExpiringInsurance: number;
    vehiclesWithValidInsurance: number;
  }>> {
    return this.request('/vehicles/stats');
  }

  // Daily MIS Reports APIs
  async getVehicleMISData(vehicleNumber: string): Promise<ApiResponse<any>> {
    return this.request(`/daily-mis/vehicle/${vehicleNumber}`);
  }

  async getVendorMISData(vendorName: string): Promise<ApiResponse<any>> {
    return this.request(`/daily-mis/vendor/${encodeURIComponent(vendorName)}`);
  }

  // Driver Report API
  async getDriverReport(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    licenseStatus?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    drivers: Array<{
      _id: string;
      name: string;
      phoneNumber: string;
      licenseNumber: string;
      licenseExpiry: string;
      isActive: boolean;
      totalTrips: number;
      completedTrips: number;
      onTimePercentage: number;
      complaints: number;
      createdAt: string;
    }>;
    totalCount: number;
    currentPage: number;
    totalPages: number;
  }>> {
    console.log('Getting driver report from API...');

    let endpoint = '/driver-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.licenseStatus) queryParams.append('licenseStatus', params.licenseStatus);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<{
      drivers: Array<{
        _id: string;
        name: string;
        phoneNumber: string;
        licenseNumber: string;
        licenseExpiry: string;
        isActive: boolean;
        totalTrips: number;
        completedTrips: number;
        onTimePercentage: number;
        complaints: number;
        createdAt: string;
      }>;
      totalCount: number;
      currentPage: number;
      totalPages: number;
    }>(endpoint);

    console.log('Get driver report response:', response);
    return response;
  }

  // Enhanced Delivery Report API
  async getEnhancedDeliveryReport(params?: {
    reportDate?: string;
    vehicleType?: string;
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    vendor: {
      id: string;
      name: string;
      email: string;
      phone: string;
      city: string;
      state: string;
    };
    reportDate: string;
    summary: {
      totalVehicles: number;
      vehiclesWithAssignments: number;
      totalAssignments: number;
      totalCompleted: number;
      totalPending: number;
      totalInTransit: number;
      totalCancelled: number;
      totalDistanceKm: string;
      totalCheckpoints: number;
      averageCompletionRate: string;
      averageTripDistance: string;
      vehicleTypes: {
        truck: number;
        van: number;
        bike: number;
        other: number;
      };
      performanceMetrics: {
        highPerformance: number;
        mediumPerformance: number;
        lowPerformance: number;
        inactive: number;
      };
    };
    vehicles: Array<{
      vehicleNumber: string;
      vehicleType: string;
      vehicleModel: string;
      vehicleCapacity: string;
      vehicleStatus: string;
      insuranceExpiry: string;
      registrationExpiry: string;
      totalAssignments: number;
      completed: number;
      pending: number;
      inTransit: number;
      cancelled: number;
      totalDistanceKm: number;
      totalCheckpoints: number;
      averageTripDistance: string;
      completionRate: string;
      assignments: Array<{
        id: string;
        startPoint: string;
        endPoint: string;
        startCoordinates: { lati: number; longi: number };
        endCoordinates: { lati: number; longi: number };
        companyName: string;
        consignorName: string;
        storeName: string;
        pickupTime: string;
        deliveryStatus: string;
        deliveryCompleted: boolean;
        tripStarted: boolean;
        delayStatus: boolean;
        delayTime: number;
        vehicleInStore: boolean;
        tempRequired: boolean;
        tempMax: number;
        tempMin: number;
        tempAbuse: boolean;
        natureofGoods: string;
        managerName: string;
        managerNumber: string;
        driverNumber: string;
        driverInfo: {
          name: string;
          phoneNumber: string;
          drivingLicense: string;
        };
        checkpoints: Array<{
          lati: number;
          longi: number;
          name: string;
          completed: boolean;
          extraCharges: boolean;
          checkpointKm: string;
        }>;
        checkpointCount: number;
        tripDistanceKm: number;
        timerStartTime: string;
        vehicleChanges: boolean;
        lastVehicleChange: any[];
        consignmentLink: string;
        endTripDocumentImage: string;
        endTripDocumentImageUploaded: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
    }>;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalVehicles: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting enhanced delivery report from API...');

    let endpoint = '/delivery-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.reportDate) queryParams.append('reportDate', params.reportDate);
      if (params.vehicleType) queryParams.append('vehicleType', params.vehicleType);
      if (params.status) queryParams.append('status', params.status);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get enhanced delivery report response:', response);
    return response;
  }

  // Enhanced Driver Report API
  async getEnhancedDriverReport(
    params?: {
      status?: string;
      licenseStatus?: string;
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<ApiResponse<{
    drivers: Array<{
      _id: string;
      name: string;
      phoneNumber: string;
      drivingLicense: string;
      drivingLicenseExpiry: string;
      address: string;
      city: string;
      state: string;
      pincode: string;
      emergencyContact: string;
      emergencyContactNumber: string;
      bloodGroup: string;
      dateOfBirth: string;
      status: string;
      licenseStatus: string;
      totalTrips: number;
      completedTrips: number;
      pendingTrips: number;
      inTransitTrips: number;
      cancelledTrips: number;
      completionRate: string;
      totalDistanceKm: string;
      totalCheckpoints: number;
      averageTripDistance: string;
      averageCheckpointsPerTrip: string;
      recentTrips: Array<{
        vehicleNumber: string;
        route: string;
        companyName: string;
        deliveryStatus: string;
        deliveryCompleted: boolean;
        createdAt: string;
      }>;
      createdAt: string;
      updatedAt: string;
    }>;
    summary: {
      totalDrivers: number;
      activeDrivers: number;
      inactiveDrivers: number;
      assignedDrivers: number;
      expiredLicenses: number;
      expiringLicenses: number;
      validLicenses: number;
      totalTrips: number;
      totalCompletedTrips: number;
      totalPendingTrips: number;
      totalInTransitTrips: number;
      totalCancelledTrips: number;
      totalDistanceKm: string;
      totalCheckpoints: number;
      averageCompletionRate: string;
      averageTripDistance: string;
      performanceMetrics: {
        highPerformers: number;
        mediumPerformers: number;
        lowPerformers: number;
        newDrivers: number;
        topDrivers: DriverPerformance[];
      };
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalDrivers: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting enhanced driver report from API...');

    let endpoint = '/driver-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.status) queryParams.append('status', params.status);
      if (params.licenseStatus) queryParams.append('licenseStatus', params.licenseStatus);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get enhanced driver report response:', response);

    return response;
  }

  // Enhanced Trips Report API
  async getEnhancedTripsReport(params?: {
    status?: string;
    vehicleType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    trips: Array<{
      _id: string;
      tripId: string;
      driver: {
        name: string;
        phoneNumber: string;
        drivingLicense: string;
      };
      vehicle: {
        number: string;
        type: string;
        model: string;
        capacity: string;
      };
      startPoint: string;
      endPoint: string;
      startCoordinates: { lati: number; longi: number };
      endCoordinates: { lati: number; longi: number };
      route: string;
      duration: string;
      status: string;
      deliveryStatus: string;
      deliveryCompleted: boolean;
      tripStarted: boolean;
      companyName: string;
      consignorName: string;
      storeName: string;
      pickupTime: string;
      delayStatus: boolean;
      delayTime: number;
      vehicleInStore: boolean;
      tempRequired: boolean;
      tempMax: number;
      tempMin: number;
      tempAbuse: boolean;
      natureofGoods: string;
      managerName: string;
      managerNumber: string;
      timerStartTime: string;
      vehicleChanges: boolean;
      lastVehicleChange: any[];
      consignmentLink: string;
      endTripDocumentImage: string;
      endTripDocumentImageUploaded: boolean;
      checkpoints: Array<{
        lati: number;
        longi: number;
        name: string;
        completed: boolean;
        extraCharges: boolean;
        checkpointKm: string;
      }>;
      checkpointCount: number;
      tripDistanceKm: string;
      userHistoryCheckpoints: Array<{
        checkpointName: string;
        checkpointLat: number;
        checkpointLng: number;
        distanceFromPrevious: number;
        cumulativeDistance: number;
        inTime: string;
        outTime: string;
      }>;
      createdAt: string;
      updatedAt: string;
    }>;
    summary: {
      totalTrips: number;
      completedTrips: number;
      pendingTrips: number;
      inTransitTrips: number;
      cancelledTrips: number;
      completionRate: string;
      totalDistanceKm: string;
      totalCheckpoints: number;
      averageTripDistance: string;
      averageCheckpointsPerTrip: string;
      vehicleTypes: {
        truck: number;
        van: number;
        bike: number;
        other: number;
      };
      tempControlledTrips: number;
      delayedTrips: number;
      totalDelayTime: number;
      performanceMetrics: {
        onTimeTrips: number;
        delayedTrips: number;
        tempAbuseTrips: number;
        vehicleChangeTrips: number;
      };
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalTrips: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting enhanced trips report from API...');

    let endpoint = '/trips-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.status) queryParams.append('status', params.status);
      if (params.vehicleType) queryParams.append('vehicleType', params.vehicleType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get enhanced trips report response:', response);
    return response;
  }

  // Vehicle Report API
  async getVehicleReport(params?: {
    vehicleType?: string;
    vehicleStatus?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    vehicles: Array<{
      _id: string;
      vehicleNumber: string;
      vehicleType: string;
      vehicleModel: string;
      vehicleCapacity: string;
      vehicleStatus: string;
      insuranceExpiry: string;
      registrationExpiry: string;
      insuranceStatus: string;
      registrationStatus: string;
      isActive: boolean;
      performanceStatus: string;
      totalAssignments: number;
      completedAssignments: number;
      pendingAssignments: number;
      inTransitAssignments: number;
      cancelledAssignments: number;
      completionRate: string;
      totalDistanceKm: string;
      totalCheckpoints: number;
      averageTripDistance: string;
      averageCheckpointsPerTrip: string;
      recentAssignments: Array<{
        route: string;
        companyName: string;
        driverNumber: string;
        deliveryStatus: string;
        deliveryCompleted: boolean;
        createdAt: string;
      }>;
      createdAt: string;
      updatedAt: string;
    }>;
    summary: {
      totalVehicles: number;
      activeVehicles: number;
      inactiveVehicles: number;
      totalAssignments: number;
      totalCompletedAssignments: number;
      totalPendingAssignments: number;
      totalInTransitAssignments: number;
      totalCancelledAssignments: number;
      totalDistanceKm: string;
      totalCheckpoints: number;
      averageCompletionRate: string;
      averageTripDistance: string;
      vehicleTypes: {
        truck: number;
        van: number;
        bike: number;
        other: number;
      };
      performanceMetrics: {
        excellent: number;
        good: number;
        average: number;
        poor: number;
        new: number;
      };
      documentStatus: {
        insuranceExpired: number;
        insuranceExpiringSoon: number;
        registrationExpired: number;
        registrationExpiringSoon: number;
      };
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalVehicles: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting vehicle report from API...');

    let endpoint = '/vehicle-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.vehicleType) queryParams.append('vehicleType', params.vehicleType);
      if (params.vehicleStatus) queryParams.append('vehicleStatus', params.vehicleStatus);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get vehicle report response:', response);
    return response;
  }

  async checkVehicleInElixia(vehicleNumber: string): Promise<ApiResponse<CheckVehicleInElixiaResponse>> {
    console.log('Checking vehicle in Elixia:', vehicleNumber);
    return this.request<CheckVehicleInElixiaResponse>(`/elixia/check-vehicle-elixia/${vehicleNumber}`);
  }

  // Payments Report API
  async getPaymentsReport(params?: {
    startDate?: string;
    endDate?: string;
    paymentStatus?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    payments: Array<{
      _id: string;
      tripId: string;
      driver: {
        name: string;
        phoneNumber: string;
      };
      vehicle: {
        number: string;
        type: string;
      };
      route: string;
      companyName: string;
      consignorName: string;
      storeName: string;
      tripDistanceKm: string;
      tempRequired: boolean;
      delayStatus: boolean;
      delayTime: number;
      estimatedPayment: string;
      paymentStatus: string;
      deliveryCompleted: boolean;
      deliveryStatus: string;
      createdAt: string;
      updatedAt: string;
    }>;
    summary: {
      totalTrips: number;
      completedTrips: number;
      pendingTrips: number;
      cancelledTrips: number;
      totalEstimatedPayment: string;
      completedPayment: string;
      pendingPayment: string;
      totalDistanceKm: string;
      averagePaymentPerTrip: string;
      tempControlledTrips: number;
      delayedTrips: number;
      performanceMetrics: {
        onTimeTrips: number;
        delayedTrips: number;
        highValueTrips: number;
        lowValueTrips: number;
      };
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalTrips: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    console.log('Getting payments report from API...');

    let endpoint = '/payments-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get payments report response:', response);
    return response;
  }

  // Export APIs
  async exportDeliveryReport(params?: {
    reportDate?: string;
    vehicleType?: string;
    status?: string;
  }): Promise<ApiResponse<Blob>> {
    let endpoint = '/export/delivery-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.reportDate) queryParams.append('reportDate', params.reportDate);
      if (params.vehicleType) queryParams.append('vehicleType', params.vehicleType);
      if (params.status) queryParams.append('status', params.status);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    return this.request<Blob>(endpoint);
  }

  async exportDriverReport(params?: {
    status?: string;
    licenseStatus?: string;
    search?: string;
  }): Promise<ApiResponse<Blob>> {
    let endpoint = '/export/driver-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.status) queryParams.append('status', params.status);
      if (params.licenseStatus) queryParams.append('licenseStatus', params.licenseStatus);
      if (params.search) queryParams.append('search', params.search);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    return this.request<Blob>(endpoint);
  }

  async exportTripsReport(params?: {
    status?: string;
    vehicleType?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<Blob>> {
    let endpoint = '/export/trips-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.status) queryParams.append('status', params.status);
      if (params.vehicleType) queryParams.append('vehicleType', params.vehicleType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    return this.request<Blob>(endpoint);
  }

  async exportVehicleReport(params?: {
    vehicleType?: string;
    vehicleStatus?: string;
    search?: string;
  }): Promise<ApiResponse<Blob>> {
    let endpoint = '/export/vehicle-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.vehicleType) queryParams.append('vehicleType', params.vehicleType);
      if (params.vehicleStatus) queryParams.append('vehicleStatus', params.vehicleStatus);
      if (params.search) queryParams.append('search', params.search);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    return this.request<Blob>(endpoint);
  }

  async exportPaymentsReport(params?: {
    startDate?: string;
    endDate?: string;
    paymentStatus?: string;
  }): Promise<ApiResponse<Blob>> {
    let endpoint = '/export/payments-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    return this.request<Blob>(endpoint);
  }

  // Legacy Trips Report API (keeping for backward compatibility)
  async getTripsReport(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    trips: Array<{
      _id: string;
      tripId: string;
      driverName: string;
      vehicleNumber: string;
      startPoint: string;
      endPoint: string;
      startTime: string;
      endTime: string;
      duration: string;
      status: string;
      distance: number;
      fuelConsumed: number;
      createdAt: string;
    }>;
    totalCount: number;
    currentPage: number;
    totalPages: number;
  }>> {
    console.log('Getting trips report from API...');

    let endpoint = '/trips-report';
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<{
      trips: Array<{
        _id: string;
        tripId: string;
        driverName: string;
        vehicleNumber: string;
        startPoint: string;
        endPoint: string;
        startTime: string;
        endTime: string;
        duration: string;
        status: string;
        distance: number;
        fuelConsumed: number;
        createdAt: string;
      }>;
      totalCount: number;
      currentPage: number;
      totalPages: number;
    }>(endpoint);

    console.log('Get trips report response:', response);
    return response;
  }

  async getVendorAssigneReport(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    dateFilter?: string;
    startDate?: string;
    endDate?: string;
    vehicleNumber?: string;
    vehicleType?: string;
    vehicleCategory?: string;
    company?: string;
    cityName?: string;
    clientCity?: string;
    status?: string;
    search?: string;
    orderType?: string;
    tripType?: string;
    paymentStatus?: string;
    managerId?: string;
    approved?: boolean;
  }): Promise<ApiResponse<any[]>> {
    console.log('Getting vendor assigne report from API...');

    let endpoint = '/reports/assigne'; // Updated endpoint
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }

    if (queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }

    const response = await this.request<any>(endpoint);
    console.log('Get vendor assigne report - raw response:', response);

    // Backend returns { status: true, data: [...], pagination: {...} }
    // API service wraps it as { success: true, data: { status: true, data: [...], ... }, status: 200 }
    // We need to extract the actual data array

    if (response.success && response.data) {
      const backendResponse = response.data;

      // Check if backend response has the expected structure
      if (backendResponse.status && Array.isArray(backendResponse.data)) {
        // Return the data array directly
        return {
          success: true,
          status: response.status,
          data: backendResponse.data, // This is the actual assignments array
          message: backendResponse.message,
          pagination: backendResponse.pagination,
        };
      }

      // Fallback: if data is already an array (shouldn't happen, but safety check)
      if (Array.isArray(backendResponse)) {
        return {
          success: true,
          status: response.status,
          data: backendResponse,
        };
      }
    }

    // Return empty array if no valid data found
    console.warn('No valid data in response, returning empty array');
    return {
      success: false,
      status: response.status || 200,
      data: [],
      error: response.error || 'No data received from server',
    };
  }

  async getVendorDashboard(params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    skip?: number;
    includeTopDrivers?: string;
  }): Promise<ApiResponse<{
    drivers: any[];
    vehicles: any[];
    assignments: any[];
    totalDrivers: number;
    totalVehicles: number;
    totalAssignments: number;
    topDrivers: DriverPerformance[];
  }>> {
    // Build query string with optional parameters to optimize API response
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.includeTopDrivers) queryParams.append('includeTopDrivers', params.includeTopDrivers);

    const queryString = queryParams.toString();
    const endpoint = `/dashboard${queryString ? `?${queryString}` : ''}`;
    const response = await this.request<any>(endpoint);

    if (response.success && response.data) {
      const backendResponse = response.data;

      // Backend returns { success: true, data: { drivers, vehicles, assignments, ... } }
      if (backendResponse.success && backendResponse.data) {
        return {
          success: true,
          status: response.status,
          data: backendResponse.data,
        };
      }

      // Fallback: if data is directly in response
      if (backendResponse.drivers || backendResponse.vehicles || backendResponse.assignments) {
        return {
          success: true,
          status: response.status,
          data: backendResponse,
        };
      }
    }

    // Return empty data if no valid data found
    console.warn('No valid dashboard data in response, returning empty data');
    return {
      success: false,
      status: response.status || 200,
      data: {
        drivers: [],
        vehicles: [],
        assignments: [],
        totalDrivers: 0,
        totalVehicles: 0,
        totalAssignments: 0,
        topDrivers: [], // Add empty array for topDrivers
      },
      error: response.error || 'No dashboard data received from server',
    };
  }

  // Invoice Methods
  async saveInvoice(invoiceData: InvoiceData): Promise<string> {
    const data = await this.invoiceRequest('/invoices', {
      method: "POST",
      body: JSON.stringify(invoiceData),
    });

    if (data.success) {
      console.log("Saved with ID:", data.id);
      return data.id; // Store this ID for future updates
    } else {
      throw new Error(data.error || "Failed to save invoice");
    }
  }

  async updateInvoice(invoiceId: string, updatedData: Partial<InvoiceData>): Promise<any> {
    return await this.invoiceRequest(`/invoices/${invoiceId}`, {
      method: "PUT",
      body: JSON.stringify(updatedData),
    });
  }

  async getInvoices(): Promise<Invoice[]> {
    const data = await this.invoiceRequest("/invoices", {
      method: "GET"
    });
    return data.invoices; // Array of invoice objects
  }

  async extractInvoice(fileBase64: string): Promise<InvoiceData> {
    const data = await this.invoiceRequest("/extract", {
      method: "POST",
      body: JSON.stringify({ fileData: fileBase64 }),
    });

    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.error || "Failed to extract invoice data");
    }
  }
}

// Custom Error Class
export class ApiError extends Error {
  public status: number;
  public code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
